import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Upload, X, FileText, FileSpreadsheet, Image } from 'lucide-react';

/**
 * Format file size from bytes to human-readable format
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get appropriate file icon based on file extension
 */
const getFileIcon = (fileName) => {
  const ext = fileName.split('.').pop().toLowerCase();
  if (ext === 'pdf') return <FileText className="h-8 w-8 text-red-500" />;
  if (['xlsx', 'xls'].includes(ext)) return <FileSpreadsheet className="h-8 w-8 text-green-600" />;
  if (['png', 'jpg', 'jpeg'].includes(ext)) return <Image className="h-8 w-8 text-blue-500" />;
  return <FileText className="h-8 w-8 text-gray-500" />;
};

/**
 * Professional FileUpload Component
 * Supports drag-and-drop, multiple files, validation, and file management
 */
const FileUpload = ({
  files = [],
  onChange,
  maxFiles = 10,
  maxSizeMB = 10,
  accept = '.pdf,.xlsx,.xls,.png,.jpg,.jpeg',
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  /**
   * Validate individual file
   */
  const validateFile = (file) => {
    // Check file size
    if (file.size > maxSizeBytes) {
      return `File "${file.name}" exceeds ${maxSizeMB}MB limit`;
    }

    // Check file type
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    const acceptedTypes = accept.split(',').map(type => type.trim());
    if (!acceptedTypes.includes(ext)) {
      return `File type "${ext}" is not allowed. Accepted types: ${accept}`;
    }

    return null;
  };

  /**
   * Process and add new files
   */
  const handleFiles = useCallback((newFiles) => {
    setError('');

    // Check max files limit
    if (files.length + newFiles.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed. Current: ${files.length}`);
      return;
    }

    // Validate each file
    const validFiles = [];
    for (const file of newFiles) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      validFiles.push(file);
    }

    // Add files
    if (validFiles.length > 0 && onChange) {
      onChange([...files, ...validFiles]);
    }
  }, [files, maxFiles, maxSizeBytes, accept, onChange]);

  /**
   * Handle drag over event
   */
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  /**
   * Handle drag leave event
   */
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  /**
   * Handle file drop event
   */
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, [disabled, handleFiles]);

  /**
   * Handle file input change
   */
  const handleInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    handleFiles(selectedFiles);
    // Reset input value to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Remove file from list
   */
  const removeFile = (indexToRemove) => {
    if (onChange) {
      onChange(files.filter((_, index) => index !== indexToRemove));
    }
    setError('');
  };

  /**
   * Trigger file input click
   */
  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : isDragging
            ? 'border-blue-500 bg-blue-50 cursor-pointer'
            : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50 cursor-pointer'
        }`}
      >
        <Upload className={`mx-auto h-12 w-12 ${disabled ? 'text-gray-300' : 'text-gray-400'}`} />
        <p className="mt-2 text-sm text-gray-600">
          {!disabled && (
            <span className="font-semibold text-blue-600">Click to upload</span>
          )}
          {!disabled && ' or drag and drop'}
          {disabled && <span className="text-gray-400">Upload disabled</span>}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          PDF, Excel, Images up to {maxSizeMB}MB each
        </p>
        <p className="text-xs text-gray-500">
          Maximum {maxFiles} files ({files.length} uploaded)
        </p>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={accept}
          onChange={handleInputChange}
          disabled={disabled}
          className="hidden"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Uploaded Files ({files.length})
          </h4>
          <div className="space-y-2">
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {getFileIcon(file.name)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                {!disabled && (
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                    title="Remove file"
                  >
                    <X className="h-4 w-4 text-gray-500 hover:text-red-500" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

FileUpload.propTypes = {
  files: PropTypes.array,
  onChange: PropTypes.func,
  maxFiles: PropTypes.number,
  maxSizeMB: PropTypes.number,
  accept: PropTypes.string,
  disabled: PropTypes.bool,
};

export default FileUpload;
